import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import GridFilterPanel from '../../commons/filters/GridFilterPanel.jsx';
import { buildPeriodoOptions } from '../../commons/filters/periodos.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import * as Utils from '../../commons/utils/Utils';
import { PRIORIDAD } from '../../commons/constants/prioridad.js';
import { TICKET_FILTRO_PREDICTIVO_MTEK, TICKET_FILTRO_CORRECTIVO_SIN_MTEK } from '../../commons/constants/ticketFiltroSentinels.js';
import {
	TICKETS_FILTROS_STORAGE_KEY,
	buildTicketsFilterFields,
	getTicketsFilterDefaults,
} from './ticketsFilterFields.js';

/**
 * Filtros de la grilla de Tickets.
 *
 * VEC-3698: este componente pasó de tener el panel escrito a mano (35 inputs, su propio
 * estado, su propia persistencia) a ser solo la capa que ALIMENTA a <GridFilterPanel />:
 * carga las opciones de los desplegables, las normaliza a `{value, label}` y expone los
 * callbacks del filtro de Entidad. El estado, la persistencia y la serialización del query
 * son del panel compartido; ver docs/design-system/grid-filter-panel.md.
 *
 * Los nombres de campo y los tipos de control se mantuvieron iguales, así el query que
 * llega al backend no cambió. Pasar campos de simple a múltiple es la segunda etapa.
 */

const OPTIONS_INIT = {
	ticketTipo: [],
	estado: [],
	nivelAprobacionPresupuesto: [],
	servicio: [],
	prioridad: [],
	gerenciador: [],
	region: [],
	subRegion: [],
	base: [],
	pais: [],
	centroCostos: [],
	centroBeneficios: [],
	enTaller: [],
	fechaRealizado: [],
	abono: [],
	turnoAsignado: [],
	fechaPrometidaAsignada: [],
	tipoModelo: [],
	origen: [],
	transportadora: [],
	rentadora: [],
	usuarioCreacion: [],
	turno: [],
	fechaPrometida: [],
};

const TicketGridFilters = (props) => {
	const intl = useIntl();
	const [options, setOptions] = useState(OPTIONS_INIT);
	const [optionsLoading, setOptionsLoading] = useState(true);

	// VEC-3555 — filtro de Entidad/Objeto. Búsqueda incremental en el servidor: reemplaza la
	// precarga de la lista completa, que en flotas grandes eran +20.000 opciones por apertura.
	const buscarEntidades = (busqueda) =>
		Utils.getDataOrNull('/tickets/entidades/simple-search?search=' + encodeURIComponent(busqueda) + '&total=20', intl)
			.then((data) => (Array.isArray(data) ? data : []));

	// Resolución de una lista pegada: los encontrados vuelven como chips y los que no, como
	// texto. Solo matchea dominios de móvil y seriales de llanta (las personas se eligen del
	// desplegable, porque matchear por nombre es ambiguo).
	const resolverEntidades = (valores) =>
		Utils.sendData('/tickets/entidades/resolver', 'POST', { valores })
			.then((data) => (data ? data : { encontrados: [], noEncontrados: [] }));

	const fields = buildTicketsFilterFields(props.columnsEnabled, {
		onSearch: buscarEntidades,
		onResolve: resolverEntidades,
	});

	/**
	 * Traduce el filtro que impone una tarjeta de KPI —que viene como valores crudos,
	 * `{ ticketTipo: 'PREVENTIVO' }`— a las opciones que necesitan los controles del panel.
	 * La grilla no conoce las opciones (se cargan acá), así que la traducción vive de este
	 * lado. Un valor sin opción cargada se descarta: mostrar un chip sin label es peor que
	 * no mostrar nada, y el query ya lo aplicó la grilla por su cuenta.
	 *
	 * OJO con el momento: las tarjetas de KPI se pueden clickear apenas responde
	 * /tickets/estadisticas, bastante antes de que lleguen las opciones de los desplegables.
	 * Por eso la señal se retiene mientras `optionsLoading` (ver el render): si se dejara
	 * pasar, el panel la consumiría, avisaría que la procesó, la grilla limpiaría el
	 * override y al llegar las opciones ya no habría nada que aplicar — el listado filtrado
	 * por PREVENTIVO con el panel mostrando POR DEFECTO.
	 */
	const overrideState = () => {
		if (!props.overrideValues) return null;

		const estado = {};
		Object.keys(props.overrideValues).forEach((campo) => {
			const opcion = (options[campo] || []).find((o) => o.value === props.overrideValues[campo]);
			// En array porque los campos son `multiselect` desde la fase 2: un valor impuesto es una
			// lista de uno. Pasarle la opción suelta dejaría al Autocomplete múltiple con un value que
			// no es array y el control quedaría roto.
			if (opcion) estado[campo] = [opcion];
		});
		return estado;
	};

	useEffect(() => {
		setOptionsLoading(true);

		Promise.all([
			Utils.getData('/bases/select-filtered-by-user/base/'),
			Utils.getData('/modelo-tipos/select'),
			Utils.getData('/gerenciadores/select-filtered-by-user'),
			Utils.getData('/tickets/estados'),
			Utils.getData('/servicios/select'),
			Utils.getData('/regiones/select'),
			Utils.getData('/subregiones/select-all'),
			Utils.getData('/centros-costos/select'),
			Utils.getData('/cebes/simple-search'),
			Utils.getData('/paises/select'),
			Utils.getData('/tickets/origenes/select'),
			Utils.getData('/transportadoras/select'),
			Utils.getData('/rentadoras/select'),
			Utils.getData('/ticket-grid/usuarios-creacion'),
		]).then((data) => {
			const periodos = buildPeriodoOptions(intl);

			setOptions({
				ticketTipo: buildTipoOptions(intl),
				estado: buildEstadoOptions(intl, data[3]),
				nivelAprobacionPresupuesto: [
					{ value: 'N1', label: 'N1' },
					{ value: 'N2', label: 'N2' },
					{ value: 'N3', label: 'N3' },
				],
				servicio: data[4] ? data[4] : [],
				// PRIORIDAD viene con {id, nombre}: el panel espera {value, label}.
				prioridad: PRIORIDAD.map((option) => ({
					value: option.id,
					label: intl.formatMessage({
						id: 'ticketsGridFilters.prioridad_select_' + option.id,
						defaultMessage: option.nombre,
					}),
				})),
				gerenciador: data[2] ? data[2] : [],
				region: data[5] ? data[5] : [],
				subRegion: data[6] ? data[6] : [],
				base: data[0] ? data[0] : [],
				pais: data[9] ? data[9] : [],
				centroCostos: data[7] ? data[7] : [],
				centroBeneficios: data[8] ? data[8] : [],
				enTaller: [
					{ value: 'true', label: intl.formatMessage({ id: 'ticketsGridFilters.select_en_taller', defaultMessage: 'EN TALLER' }) },
					{ value: 1, label: intl.formatMessage({ id: 'ticketsGridFilters.select_si_sin_verificacion', defaultMessage: 'SI - Sin Verificación' }) },
					{ value: 2, label: intl.formatMessage({ id: 'ticketsGridFilters.select_si_verificado_GPS', defaultMessage: 'SI - Verificado GPS' }) },
					{ value: 3, label: intl.formatMessage({ id: 'ticketsGridFilters.select_no_verificado_GPS', defaultMessage: 'No - Verificado GPS' }) },
					{ value: 0, label: intl.formatMessage({ id: 'ticketsGridFilters.select_no', defaultMessage: 'NO' }) },
				],
				fechaRealizado: [
					{ value: true, label: intl.formatMessage({ id: 'ticketsGridFilters.realizado_select_si', defaultMessage: 'SI' }) },
					{ value: false, label: intl.formatMessage({ id: 'ticketsGridFilters.realizado_select_no', defaultMessage: 'NO' }) },
				],
				abono: [
					{ value: true, label: intl.formatMessage({ id: 'ticketsGridFilters.abono_select_si', defaultMessage: 'SI' }) },
					{ value: false, label: intl.formatMessage({ id: 'ticketsGridFilters.abono_select_no', defaultMessage: 'NO' }) },
				],
				turnoAsignado: [
					{ value: true, label: intl.formatMessage({ id: 'ticketsGridFilters.turno_asignado_select_si', defaultMessage: 'SI' }) },
					{ value: false, label: intl.formatMessage({ id: 'ticketsGridFilters.turno_asignado_select_no', defaultMessage: 'NO' }) },
				],
				fechaPrometidaAsignada: [
					{ value: true, label: intl.formatMessage({ id: 'ticketsGridFilters.fecha_prometida_asignada_select_si', defaultMessage: 'SI' }) },
					{ value: false, label: intl.formatMessage({ id: 'ticketsGridFilters.fecha_prometida_asignada_select_no', defaultMessage: 'NO' }) },
				],
				tipoModelo: data[1] ? data[1] : [],
				origen: data[10] ? data[10] : [],
				// Transportadoras y rentadoras traen {id, nombre} y {id, descripcion}.
				transportadora: (data[11] ? data[11] : []).map((t) => ({ value: t.id, label: t.nombre })),
				rentadora: (data[12] ? data[12] : []).map((r) => ({ value: r.id, label: r.descripcion })),
				usuarioCreacion: buildUsuarioCreacionOptions(intl, data[13]),
				turno: periodos,
				fechaPrometida: periodos,
			});
			setOptionsLoading(false);
		});
	}, []);

	return (
		<GridFilterPanel
			open={props.open}
			fields={fields}
			options={options}
			defaults={getTicketsFilterDefaults(intl)}
			storageKey={TICKETS_FILTROS_STORAGE_KEY}
			loading={props.loading || optionsLoading}
			requireAlgunFiltro={true}
			applyOnMount={true}
			resetSignal={props.resetSignal}
			onResetSignalHandled={props.onResetSignalHandled}
			overrideState={overrideState()}
			overrideSignal={optionsLoading ? null : props.overrideValues}
			onOverrideSignalHandled={props.onOverrideHandled}
			onApply={props.onApply}
		/>
	);
};

/**
 * Los tipos de ticket dependen de tres flags. Con M Tek habilitado, "Predictivos" son
 * correctivos con detalle=PREDICTIVO (ver TicketsRepository::getGridStatistics), así que el
 * filtro usa los sentinels que reconocen TicketsService::queryFilters() y
 * TicketGridNew::scopeUserSelectedFilters(). Mismo criterio que TicketCards.jsx.
 */
const buildTipoOptions = (intl) => {
	const mtekHabilitado = ConfigBusiness.get('mtek.habilitado') === 'true';
	const filtroCorrectivoTipo = mtekHabilitado ? TICKET_FILTRO_CORRECTIVO_SIN_MTEK : 'CORRECTIVO';

	const tipos = [
		{ value: 'DEFAULT', label: intl.formatMessage({ id: 'ticketsGridFilters.select_por_defecto', defaultMessage: 'POR DEFECTO' }) },
		{ value: filtroCorrectivoTipo, label: intl.formatMessage({ id: 'ticketsGridFilters.select_correctivo', defaultMessage: 'CORRECTIVO' }) },
		{ value: 'PREVENTIVO', label: intl.formatMessage({ id: 'ticketsGridFilters.select_preventivo', defaultMessage: 'PREVENTIVO' }) },
		{ value: 'VENCIMIENTO', label: intl.formatMessage({ id: 'ticketsGridFilters.select_vencimiento', defaultMessage: 'VENCIMIENTO' }) },
	];

	if (ConfigBusiness.get('mantenimientos.gestoria.habilitado') === 'true') {
		tipos.push({ value: 'GESTORIA', label: intl.formatMessage({ id: 'ticketsGridFilters.select_gestoria', defaultMessage: 'GESTORIA' }) });
	}

	if (mtekHabilitado || ConfigBusiness.get('predictivo.habilitado') === 'true') {
		tipos.push({
			value: mtekHabilitado ? TICKET_FILTRO_PREDICTIVO_MTEK : 'PREDICTIVO',
			label: intl.formatMessage({ id: 'ticketsGridFilters.select_predictivo', defaultMessage: 'PREDICTIVO' }),
		});
	}

	return tipos;
};

/** PENDIENTES es un agrupador (todo lo que no está cerrado ni cancelado), no un estado. */
const buildEstadoOptions = (intl, estadosBackend) => {
	const ticketEstados = localStorage.getItem('ticketsEstadoStrings')
		? JSON.parse(localStorage.getItem('ticketsEstadoStrings'))
		: [];

	const estados = [
		{ value: 'PENDIENTES', label: intl.formatMessage({ id: 'tickets.estados.PENDIENTES', defaultMessage: 'PENDIENTES' }) },
	];
	if (estadosBackend) estadosBackend.forEach((e) => estados.push({ value: e, label: ticketEstados[e] }));

	return estados;
};

/** Endpoint con formato estándar { codigo, mensaje, detalle, data }. */
const buildUsuarioCreacionOptions = (intl, respuesta) => {
	const usuarios = respuesta && respuesta.data ? respuesta.data : [];
	const opciones = usuarios.map((u) => ({ value: u.id, label: u.nombreApellido }));

	// Los tickets generados automáticamente no tienen usuario_creacion. El value
	// 'sin_creador' es un centinela que el backend interpreta como IS NULL.
	opciones.unshift({
		value: 'sin_creador',
		label: intl.formatMessage({
			id: 'ticketsGrid.column_usuario_creacion.generacion_automatica',
			defaultMessage: 'Generación automática',
		}),
	});

	return opciones;
};

export default TicketGridFilters;
